import { ID, Query } from "appwrite";
import { 
    Product, 
    Stream, 
    StreamPurchase, 
    ProcessLiveStreamPurchaseParams,
    StreamAnalytics
} from "@/types/liveStreamTypes";
import { appwriteConfig, client, databases, functions } from "@/lib/appwrite/config";
import { notificationService } from "./NotificationService";
import { getUserById } from "@/lib/appwrite/api";
import { getUserTrustScore } from "@/lib/appwrite/trustScore";

export const createLiveStream = async (data: {
  title: string;
  description: string;
  sellerId: string;
  sellerName: string;
  featuredProducts: string[];
  thumbnailFile?: string | string[];
  liveDiscount?: {
    percent: number;
    maxUsers: number;
  };
}) => {
  try {
    const thumbnailUrl = Array.isArray(data.thumbnailFile) 
      ? data.thumbnailFile[0] 
      : data.thumbnailFile || "";

    const liveStream = await databases.createDocument(
      appwriteConfig.databaseId,
      appwriteConfig.liveStreams,
      "unique()",
      {
        sellerId: data.sellerId,
        sellerName: data.sellerName,
        title: data.title,
        description: data.description,
        featuredProducts: data.featuredProducts,
        thumbnailUrl,
        status: "live",
        startTime: new Date().toISOString(),
        viewers: "0",
        likes: "0",
        purchases: "0",
        totalSales: "0",
        scheduledDiscountMaxUsers: data.liveDiscount?.maxUsers || null,
        scheduledDiscountPercent: data.liveDiscount?.percent || null,
        scheduledDiscountUsedCount: "0"
      }
    );

    return liveStream;
  } catch (error) {
    console.error("Error creating live stream:", error);
    throw error;
  }
};

export const applyLiveDiscount = async (
  liveStreamId: string,
) => {
  try {
    const stream = await databases.getDocument(
      appwriteConfig.databaseId,
      appwriteConfig.liveStreams,
      liveStreamId
    );

    const maxUsers = parseInt(stream.scheduledDiscountMaxUsers || '0');
    const usedCount = parseInt(stream.scheduledDiscountUsedCount || '0');
    const discountPercent = parseFloat(stream.scheduledDiscountPercent || '0');

    if (usedCount >= maxUsers || discountPercent === 0) {
      throw new Error('Discount no longer available');
    }

    await databases.updateDocument(
      appwriteConfig.databaseId,
      appwriteConfig.liveStreams,
      liveStreamId,
      {
        scheduledDiscountUsedCount: (usedCount + 1).toString()
      }
    );

    return {
      discountPercent,
      remaining: maxUsers - usedCount - 1
    };
  } catch (error) {
    console.error("Error applying live discount:", error);
    throw error;
  }
};

export const processLiveStreamPurchase = async (
    params: ProcessLiveStreamPurchaseParams
): Promise<{ purchase: StreamPurchase; order: any }> => {
    try {
        const { streamId, productId, buyerId, buyerName, quantity = 1 } = params;
        
        const [product, stream] = await Promise.all([
            databases.getDocument(
                appwriteConfig.databaseId,
                appwriteConfig.products,
                productId
            ),
            databases.getDocument(
                appwriteConfig.databaseId,
                appwriteConfig.liveStreams,
                streamId
            )
        ]);

        const totalAmount = Number(product.price) * quantity;
        const scoreData = await getUserTrustScore(product.sellerId);
        const getFeeReduction = () => {
          if (!scoreData) return 0.00;
            if (scoreData.score >= 5000) return 0.00;
            if (scoreData.score >= 3200) return 0.00;
            if (scoreData.score >= 2000) return 0.00;
            if (scoreData.score >= 1100) return 0.00;
            if (scoreData.score >= 600) return 0.00;
            return 0.00;
          }
        const commissionRate = getFeeReduction();
        const platformFee = totalAmount * commissionRate;
        const sellerAmount = totalAmount - platformFee;
        
        const cashfreeOrderId = `order_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
        
        const order = {
            cashfreeOrderId,
            amount: Number(totalAmount),
            currency: 'INR',
            productId,
            streamId,
            buyerId,
            sellerId: product.sellerId || stream.sellerId,
            escrow: true,
            commission_rate: commissionRate
        };

        const purchase = await databases.createDocument(
            appwriteConfig.databaseId,
            appwriteConfig.streamPurchases,
            ID.unique(),
            {
                streamId,
                productId,
                productName: product.name,
                productPrice: product.price.toString(),
                quantity,
                totalAmount: totalAmount.toString(),
                platformFee: platformFee.toString(),
                sellerAmount: sellerAmount.toString(),
                buyerId,
                buyerName,
                sellerId: product.sellerId || stream.sellerId,
                cashfreeOrderId,
                status: "created",
                timestamp: new Date().toISOString()
            }
        );
        
        await databases.updateDocument(
          appwriteConfig.databaseId,
          appwriteConfig.liveStreams,
          streamId,
          {
            purchases: String((Number(stream.purchases) || 0) + 1),
            totalSales: String((Number(stream.totalSales) || 0) + totalAmount)
          }
        );
        
        return { purchase, order };
    } catch (error) {
        console.error("Error processing live stream purchase:", error);
        throw error;
    }
};

export const getStreamAnalytics = async (streamId: string): Promise<Omit<StreamAnalytics, 'topProducts'> & { topProducts: Record<string, number> }> => {
    const [stream, purchases] = await Promise.all([
        databases.getDocument(
            appwriteConfig.databaseId,
            appwriteConfig.liveStreams,
            streamId
        ),
        databases.listDocuments(
            appwriteConfig.databaseId,
            appwriteConfig.streamPurchases,
            [
                Query.equal("streamId", streamId)
            ]
        )
    ]);

    return {
        totalStreams: 1,
        totalViewers: Number(stream.viewers),
        totalLikes: Number(stream.likes),
        totalPurchases: purchases.total,
        totalSalesAmount: Number(stream.totalSales),
        averageViewersPerStream: Number(stream.viewers),
        conversionRate: purchases.total > 0 ? (purchases.total / Number(stream.viewers)) * 100 : 0,
        streams: [stream],
        topProducts: purchases.documents.reduce((acc: Record<string, number>, purchase) => {
            acc[purchase.productId] = (acc[purchase.productId] || 0) + 1;
            return acc;
        }, {})
    };
};

export const getStreamById = async (streamId: string): Promise<Stream> => {
  try {
    return await databases.getDocument(
      appwriteConfig.databaseId,
      appwriteConfig.liveStreams,
      streamId
    );
  } catch (error) {
    console.error("Error getting stream by ID:", error);
    throw error;
  }
};

export const getProductById = async (featuredProductId: string): Promise<Stream> => {
  try {
    return await databases.getDocument(
      appwriteConfig.databaseId,
      appwriteConfig.liveStreams,
      featuredProductId
    );
  } catch (error) {
    console.error("Error getting stream by ID:", error);
    throw error;
  }
};

export const getStreamProducts = async (streamId: string): Promise<Product[]> => {
  try {
    const stream = await getStreamById(streamId);
    if (!stream.featuredProducts || stream.featuredProducts.length === 0) {
      return [];
    }

    const products = await databases.listDocuments(
      appwriteConfig.databaseId,
      appwriteConfig.products,
      [
        Query.equal("$id", stream.featuredProducts),
        Query.equal("isActivated", true)
      ]
    );

    return products.documents;
  } catch (error) {
    console.error("Error getting stream products:", error);
    throw error;
  }
};

export const subscribeToStreamPurchases = (
  streamId: string,
  callback: (purchase: StreamPurchase) => void
) => {
  try {
    const purchaseChannel = `databases.${appwriteConfig.databaseId}.collections.${appwriteConfig.streamPurchases}.documents`;
    
    return client.subscribe(purchaseChannel, (response: any) => {
      if (
        response.events.includes("databases.*.collections.*.documents.*.create") &&
        response.payload?.streamId === streamId
      ) {
        callback(response.payload as StreamPurchase);
      }
    });
  } catch (error) {
    console.error('Failed to set up purchase subscription:', error);
    return () => {};
  }
};

export const getSellerProducts = async (sellerId: string) => {
  try {
    const res = await databases.listDocuments(
      appwriteConfig.databaseId,
      appwriteConfig.products, 
       [
      Query.equal("sellerId", sellerId),
      Query.equal("isActivated", true)
    ]);
    return res.documents;
  } catch (err) {
    console.error("Error fetching deal:", err);
    return null;
  }
};

export const createdProduct = async (
  newProduct: {
    title: string;
    price: string;
    description: string;
    images: string[];
    sellerId: string;
  }
): Promise<Product> => {
  try {
    const created = await databases.createDocument(
      appwriteConfig.databaseId,
      appwriteConfig.products,
      ID.unique(),
      {
        title: newProduct.title,
        price: newProduct.price,
        description: newProduct.description,
        images: newProduct.images,
        sellerId: newProduct.sellerId,
        status: "active",
        isActivated: true
      }
    ) as Product;
    
    return created;
  } catch (error) {
    console.error("Product creation error:", error);
    throw error;
  }
};

export async function getLiveStreams(limit?: number) {
  const queries: any[] = [Query.orderDesc("$createdAt")];

  if (limit) {
    queries.push(Query.limit(limit));
  }

  try {
    const streams = await databases.listDocuments(
      appwriteConfig.databaseId,
      appwriteConfig.liveStreams,
      queries
    );

    if (!streams) throw Error;

    return streams;
  } catch (error) {
    console.log(error);
  }
}

export async function sendLiveChatMessage({
  streamId,
  userId,
  userName,
  userRole,
  text,
}: {
  streamId: string;
  userId: string;
  userName: string;
  userRole: string;
  text: string;
}) {
  try {
    const messageData = {
      streamId,
      userId,
      userName,
      userRole,
      text: text.trim(),
      timestamp: new Date().toISOString(),
    };

    const result = await databases.createDocument(
      appwriteConfig.databaseId,
      appwriteConfig.liveStreamsChat,
      ID.unique(),
      messageData
    );

    return result;
  } catch (error) {
    console.error("Failed to send live chat message:", error);
    throw error;
  }
}

export const scheduleLiveStream = async (data: {
  sellerId: string;
  title: string;
  description: string;
  products: string[];
  scheduledAt: string;
  discount?: { maxUsers?: number; percent?: number };
}) => {
  const result = await databases.createDocument(
    appwriteConfig.databaseId,
    appwriteConfig.scheduledLivesCollectionId,
    "unique()",
    {
      sellerId: data.sellerId,
      title: data.title,
      description: data.description,
      products: data.products,
      scheduledAt: data.scheduledAt,
      discountMaxUsers: data.discount?.maxUsers || null,
      discountPercent: data.discount?.percent || null,
      status: "upcoming",
      isActivated: true
    }
  );
  
  return result;
};

export const sendLiveStreamNotifications = async (
  sellerId: string, 
  streamTitle: string, 
  sellerName: string,
) => {
  try {
    const { documents: followers } = await databases.listDocuments(
      appwriteConfig.databaseId,
      appwriteConfig.followsCollectionId,
      [Query.equal('followingId', sellerId)]
    );

    const notificationPromises = followers.map(async (follower) => {
      return await notificationService.createNotification({
        title: "New Live Stream Scheduled",
        body: `${sellerName} has scheduled a new live stream: "${streamTitle}"`,
        type: "live_stream",
        recipient_id: follower.followerId,
        sender_id: sellerId,
      });
    });

    await Promise.all(notificationPromises);
  } catch (error) {
    console.error("Error sending notifications:", error);
    throw error;
  }
};

export const getUpcomingLives = async () => {
  try {
    const upcomingStreams = await databases.listDocuments(
      appwriteConfig.databaseId,
      appwriteConfig.scheduledLivesCollectionId,
      [
        Query.equal("status", ["upcoming"]),
        Query.equal("isActivated", true)
      ]
    );
    
    if (!upcomingStreams.documents.length) {
      return upcomingStreams;
    }

    const sellers = new Map<string, Promise<any>>();
    const enhancedStreams = await Promise.all(
      upcomingStreams.documents.map(async (stream: any) => {
        let sellerName = 'Unknown Seller';
        let productImage = '/assets/images/placeholder-product.png';
        const productIds = Array.isArray(stream.products)
          ? stream.products
          : Array.isArray(stream.featuredProducts)
            ? stream.featuredProducts
            : [];
        
        try {
          const sellerRequest = sellers.get(stream.sellerId) ?? getUserById(stream.sellerId);
          sellers.set(stream.sellerId, sellerRequest);
          const seller = await sellerRequest;
          sellerName = seller?.name || seller?.username || 'Unknown Seller';
        } catch (error) {
          console.warn(`Failed to fetch seller for stream ${stream.$id}:`, error);
        }

        if (productIds[0]) {
          try {
            const product = await databases.getDocument(
              appwriteConfig.databaseId,
              appwriteConfig.products,
              productIds[0]
            );
            productImage = product.images?.[0] || '/assets/images/placeholder-product.png';
          } catch (error) {
            console.warn(`Failed to fetch product for stream ${stream.$id}:`, error);
          }
        }

        return {
          ...stream,
          sellerName,
          productImage
        };
      })
    );

    return {
      ...upcomingStreams,
      documents: enhancedStreams
    };
  } catch (error) {
    console.error('Error fetching upcoming lives:', error);
    throw error;
  }
};

export const getScheduledStream = async (scheduledStreamId: string) => {
  try {
    const result = await databases.getDocument(
      appwriteConfig.databaseId,
      appwriteConfig.scheduledLivesCollectionId,
      scheduledStreamId
    );
    return result;
  } catch (error) {
    console.error("Error fetching scheduled stream:", error);
    throw error;
  }
};

export const getSellerScheduledStreams = async (sellerId: string) => {
  try {
    const result = await databases.listDocuments(
      appwriteConfig.databaseId,
      appwriteConfig.scheduledLivesCollectionId,
      [
        Query.equal("sellerId", sellerId),
        Query.equal("status", "upcoming"),
        Query.orderDesc("scheduledAt")
      ]
    );
    return result.documents;
  } catch (error) {
    console.error("Error fetching seller scheduled streams:", error);
    throw error;
  }
};

export const createLiveStreamFromScheduled = async (data: {
  scheduledStreamId: string;
  sellerId: string;
  sellerName: string;
  title: string;
  description: string;
  featuredProducts: string[];
  thumbnailUrl?: string;
  scheduledDiscount?: {
    maxUsers: number;
    percent: number;
  };
}) => {
  try {
    const liveStream = await databases.createDocument(
      appwriteConfig.databaseId,
      appwriteConfig.liveStreams,
      "unique()",
      {
        sellerId: data.sellerId,
        sellerName: data.sellerName,
        title: data.title,
        description: data.description,
        featuredProducts: data.featuredProducts,
        thumbnailUrl: data.thumbnailUrl || "",
        status: "live",
        startTime: new Date().toISOString(),
        viewers: "0",
        likes: "0",
        purchases: "0",
        totalSales: "0",
        scheduledDiscountMaxUsers: data.scheduledDiscount?.maxUsers || null,
        scheduledDiscountPercent: data.scheduledDiscount?.percent || null,
        scheduledDiscountUsedCount: "0"
      }
    );

    await databases.updateDocument(
      appwriteConfig.databaseId,
      appwriteConfig.scheduledLivesCollectionId,
      data.scheduledStreamId,
      {
        status: "live",
        isActivated: false 
      }
    );

    return liveStream;
  } catch (error) {
    console.error("Error creating live stream from scheduled:", error);
    throw error;
  }
};

export const updateScheduledLive = async (
  scheduledStreamId: string,
  updateData: {
    title?: string;
    description?: string;
    products?: string[];
    scheduledAt?: string;
    discount?: {
      maxUsers: number;
      percent: number;
    };
  }
) => {
  try {
    const updatePayload: any = {};
    
    if (updateData.title) updatePayload.title = updateData.title;
    if (updateData.description) updatePayload.description = updateData.description;
    if (updateData.products) updatePayload.products = updateData.products;
    if (updateData.scheduledAt) updatePayload.scheduledAt = updateData.scheduledAt;
    
    if (updateData.discount) {
      updatePayload.discountMaxUsers = updateData.discount.maxUsers;
      updatePayload.discountPercent = updateData.discount.percent;
    } else {
      updatePayload.discountMaxUsers = null;
      updatePayload.discountPercent = null;
    }

    const updatedStream = await databases.updateDocument(
      appwriteConfig.databaseId,
      appwriteConfig.scheduledLivesCollectionId,
      scheduledStreamId,
      updatePayload
    );

    return updatedStream;
  } catch (error) {
    console.error("Error updating scheduled live stream:", error);
    throw error;
  }
};

export const updateScheduledDiscountUsage = async (streamId: string) => {
  try {
    const stream = await databases.getDocument(
      appwriteConfig.databaseId,
      appwriteConfig.liveStreams,
      streamId
    );

    const currentUsedCount = parseInt(stream.scheduledDiscountUsedCount || "0");
    
    await databases.updateDocument(
      appwriteConfig.databaseId,
      appwriteConfig.liveStreams,
      streamId,
      {
        scheduledDiscountUsedCount: currentUsedCount + 1
      }
    );

    return currentUsedCount + 1;
  } catch (error) {
    console.error("Error updating scheduled discount usage:", error);
    throw error;
  }
};

export const trackViewerJoin = async (streamId: string, userId: string, sessionId?: string) => {
  try {
    const response = await functions.createExecution(
      appwriteConfig.trackViewerFunctionId,
      JSON.stringify({
        action: 'join',
        streamId,
        userId,
        sessionId: sessionId || `${userId}_${Date.now()}`,
        timestamp: new Date().toISOString()
      }),
      false,
      'application/json'
    );

    if (response.responseStatusCode !== 200) {
      throw new Error(`Function failed with status ${response.responseStatusCode}: ${response.responseBody}`);
    }

    let result;
    try {
      result = JSON.parse(response.responseBody);
    } catch (parseError) {
      console.error('Failed to parse response:', response.responseBody);
      throw new Error(`Invalid JSON response: ${response.responseBody}`);
    }

    if (!result.success) {
      throw new Error(result.error || 'Failed to track viewer join');
    }

    return {
      viewerCount: result.viewerCount || 0,
      isNewViewer: result.result?.isNewViewer || false,
      stats: result.stats || {}
    };
  } catch (error) {
    console.error('Error tracking viewer join:', error);
    
    return {
      viewerCount: 1,
      isNewViewer: true,
      stats: {}
    };
  }
};

export const trackViewerLeave = async (streamId: string, userId: string, sessionId?: string) => {
  try {
    const response = await functions.createExecution(
      appwriteConfig.trackViewerFunctionId,
      JSON.stringify({
        action: 'leave',
        streamId,
        userId,
        sessionId,
        timestamp: new Date().toISOString()
      }),
      false,
      'application/json'
    );

    if (response.responseStatusCode !== 200) {
      console.warn('Viewer leave failed (non-critical):', response.responseBody);
      return { viewerCount: 0, sessionDuration: 0, stats: {} };
    }

    let result;
    try {
      result = JSON.parse(response.responseBody);
    } catch (parseError) {
      console.warn('Failed to parse leave response (non-critical):', response.responseBody);
      return { viewerCount: 0, sessionDuration: 0, stats: {} };
    }

    if (!result.success) {
      console.warn('Viewer leave unsuccessful (non-critical):', result.error);
      return { viewerCount: 0, sessionDuration: 0, stats: {} };
    }

    return {
      viewerCount: result.viewerCount || 0,
      sessionDuration: result.result?.sessionDuration || 0,
      stats: result.stats || {}
    };
  } catch (error) {
    console.warn('Error tracking viewer leave (non-critical):', error);
    return { viewerCount: 0, sessionDuration: 0, stats: {} };
  }
};

export const sendViewerHeartbeat = async (streamId: string, userId: string, sessionId?: string) => {
  try {
    const response = await functions.createExecution(
      appwriteConfig.trackViewerFunctionId,
      JSON.stringify({
        action: 'heartbeat',
        streamId,
        userId,
        sessionId,
        timestamp: new Date().toISOString()
      }),
      false,
      'application/json'
    );

    if (response.responseStatusCode !== 200) {
      console.warn('Heartbeat failed (non-critical):', response.responseBody);
      return null;
    }

    const result = JSON.parse(response.responseBody);
    return result.success ? result.viewerCount : null;
  } catch (error) {
    console.warn('Heartbeat error (non-critical):', error);
    return null;
  }
};

export const getStreamViewerStatus = async (streamId: string, userId: string) => {
  try {
    const response = await functions.createExecution(
      appwriteConfig.trackViewerFunctionId,
      JSON.stringify({
        action: 'get_status',
        streamId,
        userId,
        timestamp: new Date().toISOString()
      }),
      false,
      'application/json'
    );

    if (response.responseStatusCode !== 200) {
      throw new Error(`Function failed with status ${response.responseStatusCode}: ${response.responseBody}`);
    }

    const result = JSON.parse(response.responseBody);
    
    if (!result.success) {
      throw new Error(result.error || 'Failed to get viewer status');
    }

    return {
      viewerCount: result.viewerCount,
      activeViewers: result.result?.activeViewers || [],
      stats: result.stats
    };
  } catch (error) {
    console.error('Error getting viewer status:', error);
    throw error;
  }
};

export const cleanupExpiredViewers = async (streamId: string, userId: string) => {
  try {
    const response = await functions.createExecution(
      appwriteConfig.trackViewerFunctionId,
      JSON.stringify({
        action: 'cleanup',
        streamId,
        userId,
        timestamp: new Date().toISOString()
      }),
      false,
      'application/json'
    );

    if (response.responseStatusCode !== 200) {
      throw new Error(`Function failed with status ${response.responseStatusCode}: ${response.responseBody}`);
    }

    const result = JSON.parse(response.responseBody);
    
    if (!result.success) {
      throw new Error(result.error || 'Failed to cleanup expired viewers');
    }

    return {
      viewerCount: result.viewerCount,
      viewersRemoved: result.result?.viewersRemoved || 0
    };
  } catch (error) {
    console.error('Error cleaning up expired viewers:', error);
    throw error;
  }
};

export const updateStreamLikes = async (streamId: string, newLikeCount: number) => {
  try {
    await databases.updateDocument(
      appwriteConfig.databaseId,
      appwriteConfig.liveStreams,
      streamId,
      {
        likes: String(Math.max(0, newLikeCount))
      }
    );
  } catch (error) {
    console.error('Failed to update stream likes:', error);
    throw error;
  }
};

export const getCurrentViewerCount = async (streamId: string): Promise<number> => {
  try {
    const stream = await getStreamById(streamId);
    const viewerCount = parseInt(stream.viewers || '0') || 0;
    
    return viewerCount;
  } catch (error) {
    console.error('Failed to get viewer count:', error);
    return 0;
  }
};

export const subscribeToStreamStatus = (
  streamId: string,
  callback: (stream: Partial<Stream>) => void
) => {
  try {
    const streamChannel = `databases.${appwriteConfig.databaseId}.collections.${appwriteConfig.liveStreams}.documents.${streamId}`;
    
    return client.subscribe(streamChannel, (response) => {
      if (response.payload) {
        callback(response.payload);
      }
    });
  } catch (error) {
    console.error('Failed to set up stream status subscription:', error);
    return () => {};
  }
};

export const deleteScheduledLive = async (scheduledStreamId: string) => {
  try {
    await databases.deleteDocument(
      appwriteConfig.databaseId,
      appwriteConfig.scheduledLivesCollectionId,
      scheduledStreamId
    );
    
    return { success: true };
  } catch (error) {
    console.error("Error deleting scheduled live stream:", error);
    throw error;
  }
};
